import React from "react";
import { FileText, Upload, X, AlertCircle } from "lucide-react";

const PDFUploadStep = ({
  uploadedFiles,
  setUploadedFiles,
  isDragOver,
  setIsDragOver,
  showToast,
}) => {
  const MAX_FILES = 5;
  const MAX_SIZE = 25 * 1024 * 1024;

  const handleFileUpload = (files) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);
    const currentCount = uploadedFiles.length;

    if (currentCount + filesArray.length > MAX_FILES) {
      showToast(`You can upload a maximum of ${MAX_FILES} files at once`, "error");
      return;
    }

    const validFiles = [];
    const errors = [];

    for (const file of filesArray) {
      if (file.type !== "application/pdf") {
        errors.push(`${file.name}: Only PDF files are supported`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        errors.push(`${file.name}: File size must not exceed 25MB`);
        continue;
      }
      if (file.size < 100) {
        errors.push(`${file.name}: File appears to be empty or corrupted`);
        continue;
      }
      if (uploadedFiles.some((f) => f.name === file.name)) {
        errors.push(`${file.name}: This file has already been selected`);
        continue;
      }

      validFiles.push({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        id: `${file.name}-${Date.now()}-${Math.random()}`,
      });
    }

    if (errors.length > 0) showToast(errors[0], "error");
    if (validFiles.length > 0) {
      setUploadedFiles([...uploadedFiles, ...validFiles]);
      showToast(`Successfully added ${validFiles.length} file${validFiles.length > 1 ? "s" : ""}`, "success");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleFileInputChange = (e) => {
    handleFileUpload(e.target.files);
    e.target.value = "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="space-y-5 transition-colors duration-200">
      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-10 text-center transition-all ${
          isDragOver
            ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20"
            : uploadedFiles.length > 0
            ? "border-green-400 dark:border-green-500 bg-green-50/30 dark:bg-green-900/10"
            : "border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 bg-gray-50/50 dark:bg-slate-900/20"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          id="pdf-upload"
        />

        <Upload className={`w-12 h-12 mx-auto mb-4 ${uploadedFiles.length > 0 ? "text-green-500 dark:text-green-400" : "text-gray-400 dark:text-slate-500"}`} />
        <p className="text-base font-medium text-gray-700 dark:text-slate-300 mb-1">
          Drop PDFs here or click to browse
        </p>
        <label
          htmlFor="pdf-upload"
          className="inline-flex items-center justify-center h-8 mt-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 px-4 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium text-xs shadow-sm"
        >
          Select Files
        </label>
        <p className="text-xs text-gray-500 dark:text-slate-500 mt-3">
          Up to {MAX_FILES} PDFs • Max 25MB each
        </p>
      </div>

      {/* Selected Files */}
      {uploadedFiles.length > 0 && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden transition-colors duration-200">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
              {uploadedFiles.length} of {MAX_FILES} files
            </span>
            {uploadedFiles.length > 1 && (
              <button
                onClick={() => setUploadedFiles([])}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded flex items-center justify-center">
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={() => setUploadedFiles(uploadedFiles.filter((f) => f.id !== file.id))}
                  className="flex-shrink-0 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg transition-colors duration-200">
        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 dark:text-blue-200">
          <p className="font-medium mb-1">AI will extract:</p>
          <p className="text-blue-700 dark:text-blue-300/80">Titles, descriptions, priorities, and statuses from your documents.</p>
        </div>
      </div>
    </div>
  );
};

export default PDFUploadStep;
